import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import FeedbackFab from "@/components/FeedbackFab";
import NativePaywall from "@/components/NativePaywall";
import TrialOfferModal from "@/components/TrialOfferModal";
import { captureRefFromUrl } from "@/lib/refTracking";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import VesselSettings from "@/pages/VesselSettings";
import AccountSettings from "@/pages/AccountSettings";
import AdminDashboard from "@/pages/AdminDashboard";
import InviteAccept from "@/pages/InviteAccept";
import Pricing from "@/pages/Pricing";
import BillingReturn from "@/pages/BillingReturn";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Support from "@/pages/Support";
import Beta from "@/pages/Beta";
import CruiserContacts from "@/pages/CruiserContacts";
import Unsubscribe from "@/pages/Unsubscribe";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import { isNativeApp, isIOSNative } from "@/lib/platform";
import { useAuth } from "@/contexts/AuthContext";

/**
 * On native apps (iOS/Android via Capacitor), the marketing landing page is
 * not useful — captains already installed the app, they don't need to be
 * sold on it. Send them straight to /login (or /app if signed in).
 *
 * On web, keep showing the landing page at `/` for SEO + new visitors.
 */
function RootRoute() {
    const { user, loading } = useAuth();
    if (isNativeApp()) {
        if (loading) return null;  // wait for auth check
        return <Navigate to={user ? "/app" : "/login"} replace />;
    }
    return <Landing />;
}

function AppRouter() {
    const location = useLocation();
    // Emergent OAuth returns to /app#session_id=... — process synchronously before routing
    if (location.hash && location.hash.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/app/:vesselId" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/app/:vesselId/settings" element={<ProtectedRoute><VesselSettings /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            {/* Native shells must never render pricing/subscription UI (Guideline 3.1.1). */}
            <Route path="/pricing" element={isNativeApp() ? <Navigate to="/app" replace /> : <Pricing />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/support" element={<Support />} />
            <Route path="/beta" element={isIOSNative() ? <Navigate to="/app" replace /> : <Beta />} />
            <Route path="/billing/return" element={<ProtectedRoute><BillingReturn /></ProtectedRoute>} />
            <Route path="/contacts" element={isNativeApp() ? <Navigate to="/app" replace /> : <ProtectedRoute><CruiserContacts /></ProtectedRoute>} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            {/* Blog — web-only marketing surface. On native, redirect to /app
                so the store review doesn't flag deep-links to marketing. */}
            <Route path="/blog" element={isNativeApp() ? <Navigate to="/app" replace /> : <BlogIndex />} />
            <Route path="/blog/:slug" element={isNativeApp() ? <Navigate to="/app" replace /> : <BlogPost />} />
            <Route path="*" element={<RootRoute />} />
        </Routes>
    );
}

export default function App() {
    // Flag the root element so CSS can apply iOS notch / Android navbar safe-area
    // padding only on native shells (not on web). See index.css `#root[data-native]`.
    useEffect(() => {
        if (typeof document === "undefined") return;
        const root = document.getElementById("root");
        if (root && isNativeApp()) {
            root.setAttribute("data-native", "true");
            // App Review Guideline 4 fix: WKWebView zooms the visual viewport
            // when an input with font-size < 16px is focused and stays zoomed,
            // leaving every screen "cut off at the edges". Lock the scale on
            // native shells only (web keeps pinch-zoom for accessibility).
            const meta = document.querySelector('meta[name="viewport"]');
            if (meta) {
                meta.setAttribute(
                    "content",
                    "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
                );
            }
        }
        // Persist any `?ref=marina` (or similar marketing tag) the very first
        // time a visitor lands. Attached to the user on first sign-in by
        // AuthContext.
        captureRefFromUrl();
    }, []);

    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRouter />
                <FeedbackFab />
                <NativePaywall />
                <TrialOfferModal />
            </AuthProvider>
        </BrowserRouter>
    );
}
